import { HOURS } from '../const/common';

export const WEB_BANNERS_DISMISSAL_STORAGE_KEY = 'cleverpush-webbanners-dismissal';
export const WEB_BANNERS_DISMISSAL_SESSION_KEY = 'cleverpush-webbanners-dismissal-session';

const VALID_BEHAVIORS = new Set(['permanent', 'session', 'timed']);

/** Stable string id for storage keys (config _id may be non-string in edge cases). */
export function bannerDismissalId(banner) {
  const raw = banner?._id;
  if (raw == null || raw === '') {
    return '';
  }
  if (typeof raw === 'object' && raw.$oid) {
    return String(raw.$oid);
  }
  return String(raw);
}

export function normalizeDismissalBehavior(value) {
  if (value == null || value === '') {
    return 'legacy';
  }
  const v = typeof value === 'string' ? value.trim().toLowerCase() : value;
  if (VALID_BEHAVIORS.has(v)) {
    return v;
  }

  return 'legacy';
}

export function dismissalDurationHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return 24;
  }
  return n;
}

function parseJson(raw, fallback) {
  if (!raw) {
    return fallback;
  }
  try {
    return JSON.parse(raw);
  } catch (_e) {
    return fallback;
  }
}

function pruneChannelRecord(rec) {
  if (!rec || typeof rec !== 'object') {
    return null;
  }
  if (rec.type === 'timed' && typeof rec.until === 'number' && Date.now() >= rec.until) {
    return null;
  }
  return rec;
}

export function readLocalDismissalRoot() {
  const root = parseJson(localStorage.getItem(WEB_BANNERS_DISMISSAL_STORAGE_KEY), {});
  const base = root && typeof root === 'object' ? root : {};
  const next = {};
  let mutated = false;

  Object.keys(base).forEach((bannerId) => {
    const entry = base[bannerId];
    if (!entry || typeof entry !== 'object') {
      mutated = true;
      return;
    }
    const newEntry = {};
    ['close', 'engagement'].forEach((ch) => {
      const pruned = pruneChannelRecord(entry[ch]);
      if (pruned) {
        newEntry[ch] = pruned;
      } else if (entry[ch]) {
        mutated = true;
      }
    });
    if (Object.keys(newEntry).length > 0) {
      next[bannerId] = newEntry;
    } else if (Object.keys(entry).length > 0) {
      mutated = true;
    }
  });

  if (mutated) {
    try {
      localStorage.setItem(WEB_BANNERS_DISMISSAL_STORAGE_KEY, JSON.stringify(next));
    } catch (_e) { /* ignore */ }
  }
  return next;
}

function readSessionDismissalRoot() {
  const root = parseJson(sessionStorage.getItem(WEB_BANNERS_DISMISSAL_SESSION_KEY), {});
  return root && typeof root === 'object' ? root : {};
}

function isSessionChannelActive(bannerId, channel) {
  const root = readSessionDismissalRoot();
  const entry = root[bannerId];
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  return Boolean(entry[channel]);
}

function isLocalChannelActive(bannerId, channel, root, behavior) {
  const entry = root[bannerId];
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  const rec = entry[channel];
  if (!rec || typeof rec !== 'object') {
    return false;
  }
  const timedOk = rec.type === 'timed' && typeof rec.until === 'number' && Date.now() < rec.until;
  if (behavior === 'timed') {
    return timedOk;
  }
  if (behavior === 'permanent') {
    return rec.type === 'permanent' || timedOk;
  }
  return false;
}

/**
 * True if stored dismissal for this channel is still active.
 * Honors dismissal storage only while the channel is configured for that storage mode, so changing the
 * dashboard from permanent → legacy/session/timed does not leave a stale local permanent
 * row blocking the banner forever.
 */
export function isDismissalChannelActive(banner, channel) {
  const id = bannerDismissalId(banner);
  if (!id) {
    return false;
  }

  const behavior = channel === 'close'
    ? normalizeDismissalBehavior(banner?.closeDismissalBehavior)
    : normalizeDismissalBehavior(banner?.engagementDismissalBehavior);

  if (behavior === 'legacy') {
    return false;
  }

  if (behavior === 'session') {
    return isSessionChannelActive(id, channel);
  }

  // permanent or timed: persisted in localStorage only (ignore stale session flags)
  const root = readLocalDismissalRoot();
  return isLocalChannelActive(id, channel, root, behavior);
}

/**
 * Either close or engagement dismissal still active (local + session dismissal storage).
 * Runs for PREVIEW as well so dismissal survives refresh while testing in the dashboard.
 */
export function isWebBannerSuppressedByDismissal(banner) {
  if (!bannerDismissalId(banner)) {
    return false;
  }
  if (isDismissalChannelActive(banner, 'close')) {
    return true;
  }
  if (isDismissalChannelActive(banner, 'engagement')) {
    return true;
  }
  return false;
}

export function recordWebBannerDismissal(banner, channel) {
  const id = bannerDismissalId(banner);
  if (!id) {
    return;
  }
  const behavior = channel === 'close'
    ? normalizeDismissalBehavior(banner.closeDismissalBehavior)
    : normalizeDismissalBehavior(banner.engagementDismissalBehavior);
  if (behavior === 'legacy') {
    return;
  }

  if (behavior === 'session') {
    try {
      const root = { ...readSessionDismissalRoot() };
      const prev = root[id] && typeof root[id] === 'object' ? root[id] : {};
      root[id] = { ...prev, [channel]: true };
      sessionStorage.setItem(WEB_BANNERS_DISMISSAL_SESSION_KEY, JSON.stringify(root));
    } catch (_e) { /* ignore */ }
    return;
  }

  const root = readLocalDismissalRoot();
  const prevEntry = root[id] && typeof root[id] === 'object' ? root[id] : {};
  let record;
  if (behavior === 'permanent') {
    record = { type: 'permanent' };
  } else {
    const hours = channel === 'close'
      ? dismissalDurationHours(banner.closeDismissalDurationHours)
      : dismissalDurationHours(banner.engagementDismissalDurationHours);
    record = { type: 'timed', until: Date.now() + hours * HOURS };
  }
  const next = {
    ...root,
    [id]: { ...prevEntry, [channel]: record },
  };
  try {
    localStorage.setItem(WEB_BANNERS_DISMISSAL_STORAGE_KEY, JSON.stringify(next));
  } catch (_e) { /* ignore */ }
}
